import type { OrderFulfillmentKind, OrderItem, OrderStatus } from '@/types/order';

export type { OrderFulfillmentKind };

function formatTextFromItem(item: OrderItem): string {
  return item.attributes
    .map((attribute) => `${attribute.name} ${attribute.value}`)
    .join(' ')
    .toLowerCase();
}

function isWebBasedFormatText(text: string): boolean {
  return /web\s*based/.test(text) || /webbased/.test(text);
}

function isEflipFormatText(text: string): boolean {
  if (isWebBasedFormatText(text)) return false;
  return (
    /eflip/.test(text) ||
    /e[\s-]?flip/.test(text) ||
    /eflipbook/.test(text) ||
    /\bdigital\b/.test(text)
  );
}

/**
 * Print vs eFlipBook vs Web Based annual.
 * Web Based wins if present; else eFlipBook (including Print + eFlipBook); else Print.
 * Also falls back to SKU hints when attributes were missing on older orders.
 */
export function resolveOrderFulfillmentKind(items: OrderItem[] | undefined): OrderFulfillmentKind {
  if (!items?.length) return 'print';

  const texts = items.map((item) => {
    const fromAttributes = formatTextFromItem(item);
    const fromSku = (item.sku || '').toLowerCase();
    return `${fromAttributes} ${fromSku}`.trim();
  });

  if (texts.some(isWebBasedFormatText)) return 'webbased';
  if (texts.some(isEflipFormatText)) return 'eflipbook';

  // SKU suffix hints used by admin templates (e.g. -IE = India eFlipbook, -IW = web).
  if (items.some((item) => /-(iw|we|wb)\b/i.test(item.sku || ''))) return 'webbased';
  if (items.some((item) => /-(ie|ef)\b/i.test(item.sku || ''))) return 'eflipbook';

  return 'print';
}

export const PRINT_STATUS_OPTIONS: { label: string; value: OrderStatus }[] = [
  { label: 'Order Placed', value: 'pending' },
  { label: 'Order Accepted', value: 'confirmed' },
  { label: 'Under Preparation', value: 'processing' },
  { label: 'Dispatch Awaited', value: 'packed' },
  { label: 'Dispatched', value: 'shipped' },
  { label: 'Received', value: 'delivered' },
  { label: 'Confirmed & Closed', value: 'closed' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Returned', value: 'returned' },
];

/** Matches eFlipBook book-status mockup exactly. */
export const EFLIPBOOK_STATUS_OPTIONS: { label: string; value: OrderStatus }[] = [
  { label: 'Order Placed', value: 'pending' },
  { label: 'Order Accepted', value: 'confirmed' },
  { label: 'eFlipBook Under Preparation', value: 'processing' },
  { label: 'eFlipBook Under Checking', value: 'packed' },
  { label: 'eFlipBook Under Creation', value: 'creating' },
  { label: 'eFlipBook Created', value: 'shipped' },
  { label: 'eFlipBook Emailed', value: 'delivered' },
  { label: 'eFlipBook Installed', value: 'installed' },
  { label: 'Confirmed & Closed', value: 'closed' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Returned', value: 'returned' },
];

/** Matches Web Based book-status mockup exactly. */
export const WEBBASED_STATUS_OPTIONS: { label: string; value: OrderStatus }[] = [
  { label: 'Order Placed', value: 'pending' },
  { label: 'Order Accepted', value: 'confirmed' },
  { label: 'eFlipBook Under Preparation', value: 'processing' },
  { label: 'eFlipBook Under Checking', value: 'packed' },
  { label: 'eFlipBook Under Creation', value: 'creating' },
  { label: 'eFlipBook Created', value: 'shipped' },
  { label: 'eFlipBook Details Emailed', value: 'delivered' },
  { label: 'eFlipBook Viewed First Time', value: 'installed' },
  { label: 'Confirmed & Closed', value: 'closed' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Returned', value: 'returned' },
];

export function getOrderStatusOptions(kind: OrderFulfillmentKind) {
  if (kind === 'webbased') return WEBBASED_STATUS_OPTIONS;
  if (kind === 'eflipbook') return EFLIPBOOK_STATUS_OPTIONS;
  return PRINT_STATUS_OPTIONS;
}

export function getOrderStatusLabel(
  status: OrderStatus | string,
  kind: OrderFulfillmentKind,
): string {
  const options = getOrderStatusOptions(kind);
  return options.find((option) => option.value === status)?.label ?? status;
}

export function isDigitalFulfillment(kind: OrderFulfillmentKind): boolean {
  return kind === 'eflipbook' || kind === 'webbased';
}

export function getFulfillmentKindLabel(kind: OrderFulfillmentKind): string {
  if (kind === 'eflipbook') return 'eFlipBook';
  if (kind === 'webbased') return 'Web Based';
  return 'Print';
}

export function getFulfillmentKindColor(kind: OrderFulfillmentKind): string {
  if (kind === 'eflipbook') return 'purple';
  if (kind === 'webbased') return 'cyan';
  return 'blue';
}
